/**
 * Formatters — all currency-aware. Mono is INR-only for v1.
 * All monetary values on the wire are in the smallest unit (paise, 1/100 rupee).
 */

const INR_NUMBER = new Intl.NumberFormat("en-IN");

/**
 * Format paise → "₹XX,XXX" (no decimals, Indian comma grouping).
 * 2240000 paise (22,400 rupees) → "₹22,400"
 */
export function formatPaiseAsRupees(paise: number): string {
  const rupees = Math.round(paise / 100);
  return `₹${INR_NUMBER.format(rupees)}`;
}

/**
 * Format a plain integer with Indian comma grouping (no currency symbol).
 * 12345 → "12,345"
 */
export function formatInteger(n: number): string {
  return INR_NUMBER.format(n);
}

/**
 * Format a percentage — 0.123 → "12%" (rounded).
 * Accepts either 0..1 (fraction) or 0..100 (already a percent).
 * Treats values > 1 as already a percent.
 */
export function formatPercent(value: number): string {
  const pct = value > 1 ? value : value * 100;
  return `${Math.round(pct)}%`;
}

/**
 * Format an ISO timestamp → "HH:mm" in user local time.
 */
export function formatClockTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--:--";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

/**
 * Human "X min ago" / "X hr ago" relative to now.
 * Null input → "never".
 */
export function formatRelative(iso: string | null, now: Date = new Date()): string {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "never";
  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 0) return "just now";
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days} d ago`;
}

/**
 * Returns true if the timestamp is older than thresholdMins.
 * Null treated as stale.
 */
export function isStale(
  iso: string | null,
  thresholdMins = 30,
  now: Date = new Date()
): boolean {
  if (!iso) return true;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return true;
  return now.getTime() - d.getTime() > thresholdMins * 60_000;
}
